import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Radio, CalendarClock, BarChart3, ShieldCheck, Cpu, Bell, Layers, GitBranch, Gauge } from "lucide-react";
import { AtlasAssistantPanel } from "@/components/atlas/AtlasAssistantPanel";
import { webAppHref, webAppIsExternal } from "@/lib/links";
import { useSession } from "@/context/SessionContext";
import { useI18n } from "@/i18n";

function Wrap({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-7xl px-4 py-14 lg:px-8 lg:py-16">{children}</div>;
}
function Head({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">{title}</h1>
      {sub && <p className="mt-4 text-base text-muted-foreground">{sub}</p>}
    </motion.div>
  );
}

export function ProductPage() {
  const { lang } = useI18n();
  const items = [
    { icon: Radio, h: lang === "tr" ? "Canlı Zekâ" : "LIVE Intelligence", p: lang === "tr" ? "Dakika, skor, momentum, baskı farkı, şut ve korner verisiyle canlı maç içgörüleri." : "Live match insights with minute, score, momentum, pressure gap, shots and corners." },
    { icon: CalendarClock, h: lang === "tr" ? "Maç Öncesi" : "Prematch", p: lang === "tr" ? "Genelleştirilmiş kanıta dayalı politika ile dinamik market evreni." : "A dynamic market universe with a generalized, evidence-driven policy." },
    { icon: Sparkles, h: "ATLAS", p: lang === "tr" ? "Konuşarak maç, market ve momentum keşfi." : "Explore matches, markets and momentum by conversation." },
    { icon: Layers, h: lang === "tr" ? "Dinamik Marketler" : "Dynamic Markets", p: lang === "tr" ? "Merkezî market registry; bilinmeyen gelecekteki marketler bile zarifçe render edilir." : "A central market registry; even unknown future markets render gracefully." },
    { icon: GitBranch, h: lang === "tr" ? "Sonuç Takibi" : "Result Tracking", p: lang === "tr" ? "Model çıktıları izlenir ve kaydedilir." : "Model outputs are tracked and recorded." },
    { icon: BarChart3, h: lang === "tr" ? "Performans" : "Performance", p: lang === "tr" ? "Yalnızca gerçek, sonuçlanan verilerden hesaplanan metrikler." : "Metrics computed only from real, resolved data." },
    { icon: Bell, h: lang === "tr" ? "Bildirimler" : "Notifications", p: lang === "tr" ? "Canlı fırsatlar, sonuçlar ve sistem uyarıları." : "Live opportunities, results and system alerts." },
    { icon: ShieldCheck, h: lang === "tr" ? "Güven" : "Trust", p: lang === "tr" ? "Bahisçi değil, kumar değil — yalnızca zekâ." : "Not a bookmaker, not gambling — intelligence only." },
  ];
  return (
    <Wrap>
      <Head eyebrow={lang === "tr" ? "Ürün" : "Product"} title={lang === "tr" ? "Premium spor zekâsı, uçtan uca" : "Premium sports intelligence, end to end"}
        sub={lang === "tr" ? "SDIP AI'nin canlı ve maç öncesi analizlerini ve ATLAS asistanını tek bir premium deneyimde birleştirir." : "The product uniting SDIP AI's live and prematch engines, the ATLAS assistant and a transparent result lifecycle in one premium experience."} />
      <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((it, i) => (
          <motion.div key={it.h} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }} className="panel panel-hover p-5">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-atlas/10 text-atlas-400"><it.icon className="h-5 w-5" /></span>
            <h3 className="mt-4 text-base font-semibold text-foreground">{it.h}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{it.p}</p>
          </motion.div>
        ))}
      </div>
    </Wrap>
  );
}

export function AtlasPublic() {
  const { d, lang } = useI18n();
  return (
    <Wrap>
      <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
        <div>
          <Head eyebrow="ATLAS" title={d.atlas.title} sub={lang === "tr" ? "Genel bir sohbet botu değil — canlı spor analitiğina bağlı bir asistan. Maçları, marketleri ve momentumu konuşarak keşfedin." : "Not a generic chatbot — an assistant wired to the live sports intelligence engine. Explore matches, markets and momentum by conversation."} />
          <div className="mt-6 flex flex-wrap gap-2">
            {d.atlas.suggestions.map((s) => (
              <span key={s} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-muted-foreground">{s}</span>
            ))}
          </div>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-6 py-3 text-sm font-bold text-atlas-ink transition hover:shadow-glow">{d.common.getStarted}</Link>
        </div>
        <AtlasAssistantPanel compact />
      </div>
    </Wrap>
  );
}

export function PerformancePublic() {
  const { lang } = useI18n();
  return (
    <Wrap>
      <Head eyebrow={lang === "tr" ? "Performans & Öğrenme" : "Performance & Learning"} title={lang === "tr" ? "Şeffaf sonuç döngüsü" : "A transparent result lifecycle"}
        sub={lang === "tr" ? "Her sinyal PENDING olarak başlar ve WIN ya da LOSE olarak sonuçlanır. Sonuçlanan her sinyal öğrenme motorunu besler. Performans metrikleri yalnızca gerçek, sonuçlanan verilerden hesaplanır." : "Every signal starts PENDING and resolves to WIN or LOSE. Each resolved signal feeds the learning engine. Performance metrics are computed only from real, resolved data."} />
      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {[
          { icon: Gauge, h: "PENDING", p: lang === "tr" ? "Aktif sinyal; orijinal market, oran ve dakika kaydedilir." : "Active signal; original market, odds and minute recorded.", accent: "text-gold-400" },
          { icon: BarChart3, h: "WIN", p: lang === "tr" ? "Nihai skor ile çözülen başarı durumu." : "Resolved success state with final score.", accent: "text-atlas-400" },
          { icon: Cpu, h: "LOSE", p: lang === "tr" ? "Net biçimde çözülen negatif durum." : "A clearly resolved negative state.", accent: "text-live-400" },
        ].map((c) => (
          <div key={c.h} className="panel p-6">
            <span className={`inline-flex items-center gap-2 text-sm font-bold ${c.accent}`}><c.icon className="h-4 w-4" /> {c.h}</span>
            <p className="mt-3 text-sm text-muted-foreground">{c.p}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 panel p-6 text-sm text-muted-foreground">
        {lang === "tr" ? "Gerçek performans metriklerini görmek için hesabınızı bağlayın. SDIP AI hiçbir zaman uydurma kazanç oranı veya başarı yüzdesi göstermez." : "Connect your account to see real performance metrics. SDIP AI never shows fabricated win rates or success percentages."}
      </div>
    </Wrap>
  );
}

export function PricingPage() {
  const { lang } = useI18n();
  const { isAuthenticated } = useSession();
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const trialHref = isAuthenticated ? "/app/membership" : "/login";
  const plans = [
    { key: "FREE", mo: "£0", yr: "£0", highlight: false, featsTr: ["Genel Bakış", "Sınırlı ürün preview", "Sınırlı erişim"], featsEn: ["Overview", "Limited product preview", "Limited access"] },
    { key: "PRO", mo: "£9.99", yr: "£79.99", highlight: true, featsTr: ["Gerçek zamanlı canlı sinyaller", "Maç öncesi sinyaller", "Sinyal geçmişi", "Temel/gelişmiş ATLAS kullanımı"], featsEn: ["Real-time live signals", "Prematch signals", "Signal history", "Basic/advanced ATLAS usage"] },
    { key: "ELITE", mo: "£19.99", yr: "£159.99", highlight: false, featsTr: ["Tüm PRO özellikleri", "Gelişmiş ATLAS analizleri", "ATLAS Coach", "Daha yüksek kota", "Gelişmiş analiz", "Öncelikli destek"], featsEn: ["Everything in PRO", "Advanced ATLAS analysis", "ATLAS Coach", "Higher quota", "Advanced analysis", "Priority support"] },
    { key: "PREMIUM", mo: "£29.99", yr: "£239.99", highlight: false, featsTr: ["Tüm ELITE özellikleri", "ATLAS Kupon Merkezi", "AI kuponları ve kupon geçmişi", "En yüksek ATLAS kotası", "Gelişmiş market intelligence"], featsEn: ["Everything in ELITE", "ATLAS Coupon Center", "AI coupons and coupon history", "Highest ATLAS quota", "Advanced market intelligence"] },
  ];
  return (
    <Wrap>
      <div className="text-center">
        <Head eyebrow={lang === "tr" ? "Fiyatlandırma" : "Pricing"} title={lang === "tr" ? "Zekâ seviyeni seç" : "Choose your intelligence tier"} />
      </div>

      {/* trial offer banner */}
      <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-atlas/25 bg-atlas/[0.06] p-6 text-center" data-testid="pricing-trial-banner">
        <h2 className="text-lg font-bold text-foreground">{lang === "tr" ? "SDIP AI'ı 3 gün ücretsiz dene" : "Try SDIP AI free for 3 days"}</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {lang === "tr" ? "72 saat tam ELITE erişim · Kart gerekmez · Deneme sonunda ödeme yapmazsan hesabın FREE plana döner." : "72 hours of full ELITE access · No card required · If you don't pay when the trial ends, your account returns to FREE."}
        </p>
        <Link to={trialHref} data-testid="pricing-trial-cta" className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-6 py-3 text-sm font-bold text-atlas-ink transition hover:shadow-glow">
          <Sparkles className="h-4 w-4" /> {lang === "tr" ? "3 Gün Ücretsiz Başla" : "Start 3-Day Free Trial"}
        </Link>
      </div>

      {/* billing period toggle */}
      <div className="mt-8 flex items-center justify-center gap-2" data-testid="pricing-period-toggle">
        {(["monthly", "yearly"] as const).map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`pricing-period-${p}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${period === p ? "bg-atlas-500 text-atlas-ink" : "border border-white/12 text-muted-foreground hover:text-foreground"}`}>
            {p === "monthly" ? (lang === "tr" ? "Aylık" : "Monthly") : (lang === "tr" ? "Yıllık" : "Yearly")}
          </button>
        ))}
      </div>

      <div className="mx-auto mt-6 grid max-w-6xl gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => {
          const price = period === "monthly" ? p.mo : p.yr;
          const paid = p.key !== "FREE";
          return (
            <div key={p.key} className={`panel relative flex flex-col p-6 ${p.highlight ? "ring-1 ring-atlas/40" : ""}`} data-testid={`pricing-${p.key.toLowerCase()}`}>
              {p.highlight && <span className="absolute -top-3 left-6 rounded-full bg-atlas/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-atlas-300">{lang === "tr" ? "Popüler" : "Popular"}</span>}
              <h3 className="text-lg font-bold text-foreground">{p.key}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="num text-3xl font-extrabold text-foreground">{price}</span>
                {paid && <span className="mb-1 text-xs text-muted-foreground">/{period === "monthly" ? (lang === "tr" ? "ay" : "mo") : (lang === "tr" ? "yıl" : "yr")}</span>}
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {(lang === "tr" ? p.featsTr : p.featsEn).map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-atlas-400" /> {f}</li>)}
              </ul>
              <Link to={trialHref} data-testid={`pricing-cta-${p.key.toLowerCase()}`}
                className={`mt-6 rounded-full px-4 py-2.5 text-center text-sm font-bold transition ${p.highlight ? "bg-gradient-to-r from-atlas-500 to-atlas-600 text-atlas-ink hover:shadow-glow" : "border border-white/10 text-foreground hover:border-white/25"}`}>
                {p.key === "FREE" ? (lang === "tr" ? "3 Gün Ücretsiz Başla" : "Start Free Trial") : (lang === "tr" ? "Planı Seç" : "Choose Plan")}
              </Link>
            </div>
          );
        })}
      </div>
      <div className="mx-auto mt-6 max-w-2xl space-y-1.5 text-center text-xs text-muted-foreground">
        <p>{lang === "tr" ? "Fiyatlar GBP cinsindendir. Gerekli durumlarda ilgili vergiler eklenebilir." : "Prices are shown in GBP. Applicable taxes may be added where required."}</p>
        <p>{lang === "tr" ? "Ücretli abonelikler, iptal edilene kadar seçilen fatura aralığında otomatik olarak yenilenir." : "Paid subscriptions renew automatically at the selected billing interval until cancelled."}</p>
        <p>{lang === "tr" ? "Analiz ve karar desteği yalnızca. Garantili sonuç yoktur." : "Analysis and decision support only. No guaranteed outcomes."}</p>
      </div>
    </Wrap>
  );
}

export function FaqPage() {
  const { lang } = useI18n();
  const faqs = lang === "tr" ? [
    ["SDIP AI bir bahis sitesi mi?", "Hayır. SDIP AI bahis kabul etmez, fon tutmaz ve kumar hizmeti sunmaz. Yalnızca yapay zekâ destekli spor zekâsı ve analitik sunar."],
    ["ATLAS nedir?", "ATLAS, canlı ve maç öncesi verileri analiz eden SDIP AI çekirdek zekâ motorudur. Asistan bu motora bağlıdır."],
    ["Sinyaller nasıl sonuçlanır?", "Her sinyal PENDING başlar, ardından üretim sistemi tarafından WIN veya LOSE olarak çözülür."],
    ["Performans metrikleri gerçek mi?", "Evet. Tüm metrikler yalnızca gerçek, sonuçlanan verilerden hesaplanır; hiçbir değer uydurulmaz."],
    ["Marketler sabit mi?", "Hayır. Market evreni dinamiktir; arayüz yeni ve bilinmeyen marketleri bile zarifçe gösterir."],
    ["Ödeme nasıl çalışıyor?", "Ücretsiz plan £0'dır. Ücretli Pro ve Elite planları için commerce@sdiptechnologies.com adresinden bizimle iletişime geçin. Fiyatlar GBP cinsindendir ve iptal edilene kadar otomatik yenilenir."],
  ] : [
    ["Is SDIP AI a betting site?", "No. SDIP AI accepts no bets, holds no funds and offers no gambling. It provides AI-powered sports intelligence and analytics only."],
    ["What is ATLAS?", "ATLAS is the core SDIP AI intelligence engine analyzing live and prematch data. The assistant is wired to this engine."],
    ["How do signals resolve?", "Each signal starts PENDING and is then resolved to WIN or LOSE by the production system."],
    ["Are performance metrics real?", "Yes. All metrics are computed only from real, resolved data — nothing is fabricated."],
    ["Are markets fixed?", "No. The market universe is dynamic; the UI renders new and unknown markets gracefully."],
    ["How do payments work?", "The Free plan is £0. For the paid Pro and Elite plans, contact us at commerce@sdiptechnologies.com. Prices are in GBP and renew automatically until cancelled."],
  ];
  return (
    <Wrap>
      <Head eyebrow="FAQ" title={lang === "tr" ? "Sık sorulan sorular" : "Frequently asked questions"} />
      <div className="mt-10 grid gap-3">
        {faqs.map(([q, a]) => (
          <details key={q} className="panel group p-5" data-testid="faq-item">
            <summary className="flex cursor-pointer items-center justify-between text-base font-semibold text-foreground marker:content-none">
              {q}
              <span className="ml-4 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-white/10 text-muted-foreground transition group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 text-sm text-muted-foreground">{a}</p>
          </details>
        ))}
      </div>
    </Wrap>
  );
}
